'use strict';

/**
 * Post-Processing DoF Depth Of Field 2
 */

import {isDev, settingsData, threeJsVariables} from "../../settings";
import {BokehDepthShader, BokehShader} from "three/examples/jsm/shaders/BokehShader2";
import * as THREE from "three";
import {rotateToCamera} from "../controls/orbitControls";


export const depthOfFields2 = (scene, camera) => {
    const {postProcessing} = settingsData;

    const canvas = document.querySelector('#c');

    const postprocessing = {
        rtTextureDepth: '',
        rtTextureColor: '',
        bokeh_uniforms: '',
        distance: 100,
    };

    postProcessing.postprocessing = postprocessing;

    const depthShader = BokehDepthShader;
    const bokeh_shader = BokehShader;

    postprocessing.shaderSettings = {
        rings: 3,
        samples: 4
    };

    const materialDepth = new THREE.ShaderMaterial({
        uniforms: depthShader.uniforms,
        vertexShader: depthShader.vertexShader,
        fragmentShader: depthShader.fragmentShader
    });

    materialDepth.uniforms[ 'mNear' ].value = camera.near;
    materialDepth.uniforms[ 'mFar' ].value = camera.far;

    postprocessing.materialDepth = materialDepth;

    postprocessing.effectController = {
        enabled: postProcessing.depthOfFields2,
        jsDepthCalculation: true,
        shaderFocus: false,
        fstop: 2.2,
        maxblur: 1.0,
        showFocus: false,
        focalDepth: 2.8,
        manualdof: false,
        vignetting: false,
        depthblur: false,
        threshold: 0.5,
        gain: 2.0,
        bias: 0.5,
        fringe: 0.7,
        focalLength: 35,
        noise: true,
        pentagon: false,
        dithering: 0.0001
    };

    // postprocessing.scene = new THREE.Scene();
    // perspectiveCamera.visible = false;
    postprocessing.camera = new THREE.OrthographicCamera( canvas.clientWidth / - 2, canvas.clientWidth / 2, canvas.clientHeight / 2, canvas.clientHeight / - 2, - 10000, 10000 );
    // postprocessing.camera = perspectiveCamera;
    // postprocessing.camera.position.z = 10;
    scene.add(postprocessing.camera);

    const pars = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, format: THREE.RGBFormat };
    postprocessing.rtTextureDepth = new THREE.WebGLRenderTarget( canvas.clientWidth, canvas.clientHeight, pars );
    postprocessing.rtTextureColor = new THREE.WebGLRenderTarget( canvas.clientWidth, canvas.clientHeight, pars );

    postprocessing.bokeh_uniforms = THREE.UniformsUtils.clone( bokeh_shader.uniforms );

    postprocessing.bokeh_uniforms[ 'tColor' ].value = postprocessing.rtTextureColor.texture;
    postprocessing.bokeh_uniforms[ 'tDepth' ].value = postprocessing.rtTextureDepth.texture;
    postprocessing.bokeh_uniforms[ 'textureWidth' ].value = canvas.clientWidth;
    postprocessing.bokeh_uniforms[ 'textureHeight' ].value = canvas.clientHeight;

    postprocessing.materialBokeh = new THREE.ShaderMaterial({
        uniforms: postprocessing.bokeh_uniforms,
        vertexShader: bokeh_shader.vertexShader,
        fragmentShader: bokeh_shader.fragmentShader,
        defines: {
            RINGS: postprocessing.shaderSettings.rings,
            SAMPLES: postprocessing.shaderSettings.samples
        },
    });

    postprocessing.quad = new THREE.Mesh( new THREE.PlaneGeometry(canvas.clientWidth, canvas.clientHeight), postprocessing.materialBokeh);
    postprocessing.quad.position.x = 0;
    postprocessing.quad.position.y = 50;
    postprocessing.quad.position.z = 0;
    scene.add(postprocessing.quad);

    if (postProcessing.isActive && isDev)  {
        addGuiDoF2();
    }

    matChanger();
}

function addGuiDoF2() {
    const {gui} = settingsData;
    const {postProcessing} = settingsData;
    const postprocessing = postProcessing.postprocessing;
    const effectController = postprocessing.effectController;
    const shaderSettings = postprocessing.shaderSettings;

    const dof = gui.addFolder('DoF2');

    dof.add( effectController, 'enabled' ).onChange( matChanger );
    dof.add( effectController, 'jsDepthCalculation' ).onChange( matChanger );
    dof.add( effectController, 'shaderFocus' ).onChange( matChanger );
    dof.add( effectController, 'focalDepth', 0.0, 200.0 ).listen().onChange( matChanger );
    dof.add( effectController, 'fstop', 0.1, 22, 0.001 ).onChange( matChanger );
    dof.add( effectController, 'maxblur', 0.0, 5.0, 0.025 ).onChange( matChanger );
    dof.add( effectController, 'showFocus' ).onChange( matChanger );
    dof.add( effectController, 'manualdof' ).onChange( matChanger );
    dof.add( effectController, 'vignetting' ).onChange( matChanger );
    dof.add( effectController, 'depthblur' ).onChange( matChanger );
    dof.add( effectController, 'threshold', 0, 1, 0.001 ).onChange( matChanger );
    dof.add( effectController, 'gain', 0, 100, 0.001 ).onChange( matChanger );
    dof.add( effectController, 'bias', 0, 3, 0.001 ).onChange( matChanger );
    dof.add( effectController, 'fringe', 0, 5, 0.001 ).onChange( matChanger );
    dof.add( effectController, 'focalLength', 16, 80, 0.001 ).onChange( matChanger );
    dof.add( effectController, 'noise' ).onChange( matChanger );
    dof.add( effectController, 'dithering', 0, 0.001, 0.0001 ).onChange( matChanger );
    dof.add( effectController, 'pentagon' ).onChange( matChanger );
    dof.add( shaderSettings, 'rings', 1, 8 ).step( 1 ).onChange( shaderUpdate );
    dof.add( shaderSettings, 'samples', 1, 13 ).step( 1 ).onChange( shaderUpdate );
}
function matChanger() {
    const {postProcessing, perspectiveCamera: camera} = settingsData;
    const postprocessing = postProcessing.postprocessing;
    const effectController = postprocessing.effectController;

    for (const e in effectController) {
        if (e in postprocessing.bokeh_uniforms) {
            postprocessing.bokeh_uniforms[e].value = effectController[e];
        }
    }

    postprocessing.enabled = effectController.enabled;
    postprocessing.bokeh_uniforms[ 'znear' ].value = camera.near;
    postprocessing.bokeh_uniforms[ 'zfar' ].value = camera.far;
    camera.setFocalLength( effectController.focalLength );
}
function shaderUpdate() {
    const {postProcessing} = settingsData;
    const postprocessing = postProcessing.postprocessing;
    const shaderSettings = postprocessing.shaderSettings;

    postprocessing.materialBokeh.defines.RINGS = shaderSettings.rings;
    postprocessing.materialBokeh.defines.SAMPLES = shaderSettings.samples;
    postprocessing.materialBokeh.needsUpdate = true;
}
function linearize(depth) {
    const {perspectiveCamera: camera} = settingsData;

    const zfar = camera.far;
    const znear = camera.near;
    return -zfar * znear / (depth * (zfar - znear) - zfar);
}
function smoothstep(near, far, depth) {
    const x = saturate((depth - near) / ( far - near ));
    return x * x * ( 3 - 2 * x );
}
function saturate(x) {
    return Math.max(0, Math.min( 1, x ));
}

export const postProcessingDoF2Resize = () => {
    const {postProcessing} = settingsData;
    const {camera} = threeJsVariables;

    if (!postProcessing.isActive && !postProcessing.depthOfFields2) return;

    const postprocessing = postProcessing.postprocessing;
    const canvas = document.querySelector('#c');

    camera.aspect = canvas.clientWidth / canvas.clientHeight;
    camera.updateProjectionMatrix();

    postprocessing.windowHalfX = canvas.clientWidth / 2;
    postprocessing.windowHalfY = canvas.clientHeight / 2;

    postprocessing.rtTextureDepth.setSize(canvas.clientWidth, canvas.clientHeight);
    postprocessing.rtTextureColor.setSize(canvas.clientWidth, canvas.clientHeight);

    postprocessing.bokeh_uniforms[ 'textureWidth' ].value = canvas.clientWidth;
    postprocessing.bokeh_uniforms[ 'textureHeight' ].value = canvas.clientHeight;
}

export const updateDoF2 = () => {
    const {postProcessing, perspectiveCamera: camera} = settingsData;
    const {raycaster, mouse, scene, renderer} = threeJsVariables;
    const postprocessing = postProcessing.postprocessing;


    if (!postProcessing.isActive || !postProcessing.depthOfFields2) return;
    const effectController = postprocessing.effectController;

    if (effectController.jsDepthCalculation) {
        raycaster.setFromCamera( mouse, postprocessing.camera );

        const intersects = raycaster.intersectObjects( scene.children, true );
        const targetDistance = ( intersects.length > 0 ) ? intersects[ 0 ].distance : 1000;

        postprocessing.distance += ( targetDistance - postprocessing.distance ) * 0.03;

        const sdistance = smoothstep( camera.near, camera.far, postprocessing.distance );
        const ldistance = linearize( 1 - sdistance );

        postprocessing.bokeh_uniforms[ 'focalDepth' ].value = ldistance;
        effectController[ 'focalDepth' ] = ldistance;
    }

    if ( postProcessing.isActive &&  postProcessing.depthOfFields2) {
        const postprocessing = postProcessing.postprocessing;
        const materialDepth = postprocessing.materialDepth;
        const quad = postprocessing.quad;

        threeJsVariables.orbitControls.object = postprocessing.camera;

        quad.rotation.y = rotateToCamera(Math.PI * -0.5);

        renderer.clear();

        // render scene into texture

        renderer.setRenderTarget( postprocessing.rtTextureColor );
        renderer.clear();
        renderer.render( scene, postprocessing.camera );

        // render depth into texture

        scene.overrideMaterial = materialDepth;
        renderer.setRenderTarget( postprocessing.rtTextureDepth );
        renderer.clear();
        renderer.render( scene, postprocessing.camera );
        scene.overrideMaterial = null;

        // render bokeh composite

        renderer.setRenderTarget( null );
        renderer.render(scene, postprocessing.camera);
    } else {
        scene.overrideMaterial = null;
        renderer.setRenderTarget( null );
        renderer.clear();
        renderer.render(scene, camera);
    }

}

export const updateMousePosition = () => {
    const {postProcessing} = settingsData;
    const {mouse} = threeJsVariables;

    if (!postProcessing.isActive || !postProcessing.depthOfFields2) return;

    postProcessing.postprocessing.bokeh_uniforms[ 'focusCoords' ].value.set( mouse.x, mouse.y);
}